import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import WmsFullScreenLayout from '../shared/WmsFullScreenLayout';
import useWmsSession from '../shared/useWmsSession';
import WmsScreenAccessGate from '../shared/WmsScreenAccessGate';
import { screenService } from '../services/screenService';
import packingService, { PackingOrderDto, PackingStats, CartPackingResult } from '../services/packingService';
import { PackingModeId } from './packing/packingModes';
import PackingHome from './packing/PackingHome';
import PackingCartScan from './packing/PackingCartScan';
import PackingCartOrders from './packing/PackingCartOrders';
import PackingCartCompleted from './packing/PackingCartCompleted';
import PackingScanEntry from './packing/PackingScanEntry';
import PackingOrderFound from './packing/PackingOrderFound';
import PackingProcess from './packing/PackingProcess';
import PackingLabelPrint from './packing/PackingLabelPrint';

type PackingPhase =
  | 'home'
  | 'cart-scan'
  | 'cart-orders'
  | 'scan-entry'
  | 'order-found'
  | 'packing'
  | 'label-print'
  | 'cart-completed';

const DEFAULT_PACKING_PERMISSIONS = {
  canPicking: false,
  canPacking: true,
  canReceiving: false,
  canCycleCount: false,
  pickingScreenId: null,
  packingScreenId: null,
  receivingScreenId: null,
  cycleCountScreenId: null,
};

const WmsPackingScreen: React.FC = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const { session, logout } = useWmsSession();

  const [phase, setPhase] = useState<PackingPhase>('home');
  const [packingMode, setPackingMode] = useState<PackingModeId>('product');
  const [warehouseId, setWarehouseId] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [todayPacked, setTodayPacked] = useState(0);

  const [currentOrder, setCurrentOrder] = useState<PackingOrderDto | null>(null);
  const [orderSource, setOrderSource] = useState<'product' | 'slot'>('product');
  const [slotCode, setSlotCode] = useState<string | undefined>();

  // Araba modu durumu.
  const [cartCode, setCartCode] = useState('');
  const [cartSource, setCartSource] = useState<string | undefined>();
  const [cartOrders, setCartOrders] = useState<PackingOrderDto[]>([]);
  const [cartTotal, setCartTotal] = useState(0);

  useEffect(() => {
    if (!screenId) return;
    (async () => {
      try {
        const res = await screenService.getScreenDetail('packing', screenId);
        const data = res.data?.data || res.data;
        if (data?.warehouseId) setWarehouseId(data.warehouseId);
      } catch { /* screen not found */ }
      setLoadingScreen(false);
    })();
  }, [screenId]);

  const refreshStats = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const stats: PackingStats = await packingService.getStats(warehouseId);
      setTodayPacked(stats.todayPacked);
    } catch { /* ignore */ }
  }, [warehouseId]);

  useEffect(() => {
    if ((phase === 'home' || phase === 'scan-entry') && warehouseId) refreshStats();
  }, [phase, warehouseId, refreshStats]);

  const resetCart = useCallback(() => {
    setCartCode('');
    setCartSource(undefined);
    setCartOrders([]);
    setCartTotal(0);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setCurrentOrder(null);
    resetCart();
    setPhase('home');
  }, [logout, resetCart]);

  const handleSelectMode = useCallback((mode: PackingModeId) => {
    setPackingMode(mode);
    setCurrentOrder(null);
    if (mode === 'cart') {
      resetCart();
      setPhase('cart-scan');
    } else {
      setPhase('scan-entry');
    }
  }, [resetCart]);

  const handleCartFound = useCallback((result: CartPackingResult) => {
    setCartCode(result.cartCode);
    setCartSource(result.marketplaceSource);
    setCartOrders(result.orders);
    setCartTotal(result.orders.length);
    setPhase('cart-orders');
  }, []);

  // Ortak: siparis kalemleri gelmemisse detay cek, sonra order-found fazina gec.
  const enterOrderFound = useCallback(async (order: PackingOrderDto, source: 'product' | 'slot', sc?: string) => {
    if (!order.items || order.items.length === 0) {
      try {
        const detailed = await packingService.getOrder(order.id);
        setCurrentOrder(detailed || order);
      } catch {
        setCurrentOrder(order);
      }
    } else {
      setCurrentOrder(order);
    }
    setOrderSource(source);
    setSlotCode(sc);
    setPhase('order-found');
  }, []);

  const handleSelectCartOrder = useCallback((order: PackingOrderDto) => {
    enterOrderFound(order, order.orderType === 'Multi' ? 'slot' : 'product');
  }, [enterOrderFound]);

  // Araba listesinde barkod okutulunca: siparis eslesir, paketleme baslatilir ve
  // dogrudan etiket yazdirma ekranina gecilir (ara adimlar atlanir). Etiket yazdirilinca
  // completePacking tum kalemleri "Packed" yapar; onaylanan siparis listeden dusulur.
  const handleScanCartOrder = useCallback(async (order: PackingOrderDto) => {
    setCurrentOrder(order);
    setOrderSource(order.orderType === 'Multi' ? 'slot' : 'product');
    setSlotCode(undefined);
    try {
      await packingService.startPacking(order.id);
    } catch { /* ignore */ }
    setPhase('label-print');
  }, []);

  const handleStartPacking = useCallback(async () => {
    if (!currentOrder) return;
    try {
      await packingService.startPacking(currentOrder.id);
    } catch { /* ignore */ }
    setPhase('packing');
  }, [currentOrder]);

  const handlePackingComplete = useCallback((updatedOrder: PackingOrderDto) => {
    setCurrentOrder(updatedOrder);
    setPhase('label-print');
  }, []);

  const handleLabelComplete = useCallback(() => {
    // Etiket yuklenemedi vb. geri donus: moda gore geri git.
    if (packingMode === 'cart') setPhase('cart-orders');
    else setPhase('scan-entry');
  }, [packingMode]);

  const handlePrintDone = useCallback(() => {
    const finishedId = currentOrder?.id;
    setCurrentOrder(null);
    setSlotCode(undefined);
    refreshStats();

    if (packingMode === 'cart') {
      const remaining = cartOrders.filter((o) => o.id !== finishedId);
      setCartOrders(remaining);
      if (remaining.length === 0) {
        setPhase('cart-completed');
      } else {
        setPhase('cart-orders');
      }
    } else {
      setPhase('scan-entry');
    }
  }, [currentOrder, packingMode, cartOrders, refreshStats]);

  const handleBackFromOrder = useCallback(() => {
    setCurrentOrder(null);
    if (packingMode === 'cart') setPhase('cart-orders');
    else setPhase('scan-entry');
  }, [packingMode]);

  const handleBackHome = useCallback(() => {
    setCurrentOrder(null);
    resetCart();
    setPhase('home');
  }, [resetCart]);

  const content = loadingScreen ? (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fb' }}>
      <CircularProgress />
    </Box>
  ) : (
    <WmsFullScreenLayout
      currentScreen="packing"
      cashierName={session?.cashierName || ''}
      wmsPermissions={session?.wmsPermissions || DEFAULT_PACKING_PERMISSIONS}
      onLogout={handleLogout}
    >
      {phase === 'home' && (
        <PackingHome
          personnelName={session?.personnelName || session?.cashierName || ''}
          warehouseId={warehouseId}
          onSelectMode={handleSelectMode}
        />
      )}

      {phase === 'cart-scan' && (
        <PackingCartScan
          warehouseId={warehouseId}
          onCartFound={handleCartFound}
          onBack={handleBackHome}
        />
      )}

      {phase === 'cart-orders' && (
        <PackingCartOrders
          cartCode={cartCode}
          marketplaceSource={cartSource}
          orders={cartOrders}
          totalCount={cartTotal}
          onSelectOrder={handleSelectCartOrder}
          onScanOrder={handleScanCartOrder}
          onBack={handleBackHome}
        />
      )}

      {phase === 'cart-completed' && (
        <PackingCartCompleted
          cartCode={cartCode}
          packedCount={cartTotal}
          onScanAnother={() => { resetCart(); setPhase('cart-scan'); }}
          onHome={handleBackHome}
        />
      )}

      {phase === 'scan-entry' && (
        <PackingScanEntry
          mode={packingMode === 'slot' ? 'slot' : 'product'}
          cashierName={session?.cashierName || ''}
          warehouseId={warehouseId}
          todayPacked={todayPacked}
          onOrderFound={enterOrderFound}
          onBack={handleBackHome}
        />
      )}

      {phase === 'order-found' && currentOrder && (
        <PackingOrderFound
          order={currentOrder}
          source={orderSource}
          slotCode={slotCode}
          onBack={handleBackFromOrder}
          onStartPacking={handleStartPacking}
        />
      )}

      {phase === 'packing' && currentOrder && (
        <PackingProcess
          order={currentOrder}
          onComplete={handlePackingComplete}
          onBack={() => setPhase('order-found')}
        />
      )}

      {phase === 'label-print' && currentOrder && (
        <PackingLabelPrint
          orderId={currentOrder.id}
          onComplete={handleLabelComplete}
          onPrintDone={handlePrintDone}
          onBack={() => setPhase(packingMode === 'cart' ? 'cart-orders' : 'packing')}
        />
      )}
    </WmsFullScreenLayout>
  );

  return (
    <WmsScreenAccessGate screenType="packing" screenId={screenId || ''}>
      {content}
    </WmsScreenAccessGate>
  );
};

export default WmsPackingScreen;
